import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { saveBlobWithFileSystemAPI } from '@/shared/lib/audio-utils'
import { VoiceElement } from '@/pages/V2SPage'
import { EllipsisVertical, Pause, Play } from 'lucide-react'
import { nanoid } from 'nanoid'
import React, { useEffect, useRef, useState } from 'react'
import WaveSurfer from 'wavesurfer.js'

interface WaveformProps {
    onDelete?: () => void
    voiceElement: VoiceElement
}

const Waveform: React.FC<WaveformProps> = ({ voiceElement, onDelete }) => {
    const waveformRef = useRef<HTMLDivElement | null>(null)
    const wavesurferRef = useRef<WaveSurfer | null>(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [audioDuration, setAudioDuration] = useState<number | null>(null)

    useEffect(() => {
        if (!voiceElement.audioBlob || !waveformRef.current) return

        // Clean up previous instance
        wavesurferRef.current?.destroy()

        // Create Wavesurfer instance
        wavesurferRef.current = WaveSurfer.create({
            container: waveformRef.current,
            waveColor: '#ccc',
            progressColor: '#007bff',
            height: 50,
            barWidth: 2,
            /*  responsive: true, */
            backend: 'MediaElement',
            /*  xhr: {
                mode: 'cors',
                credentials: 'omit',
            }, */
        })
        wavesurferRef.current.loadBlob(voiceElement.audioBlob)

        wavesurferRef.current.on('ready', () => {
            console.log('WaveSurfer ready')
            const duration = wavesurferRef.current?.getDuration()
            if (duration) {
                setAudioDuration(duration)
            }
        })

        wavesurferRef.current.on('error', (err) => {
            console.error('WaveSurfer error:', err)
        })

        wavesurferRef.current.on('finish', () => {
            setIsPlaying(false)
        })

        return () => {
            wavesurferRef.current?.destroy()
        }
    }, [voiceElement])

    const togglePlay = () => {
        if (!wavesurferRef.current) return
        wavesurferRef.current.playPause()
        setIsPlaying(wavesurferRef.current.isPlaying())
    }

    const handleDelete = () => {
        wavesurferRef.current?.destroy()
        onDelete?.()
    }
    return (
        <Card className=" bg-white w-full relative border-0 shadow-none pt-5 pb-2">
            {/* Dropdown positioned top-right */}
            <div className="absolute top-2 w-full px-5">
                <span className="uppercase text-sm font-semibold"> {voiceElement.type}</span>{' '}
                {onDelete && (
                    <div className="absolute top-0 right-0 z-10">
                        <DropdownMenu key={nanoid()}>
                            <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="ghost">
                                    <EllipsisVertical />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="w-56" align="end">
                                <DropdownMenuLabel
                                    onClick={handleDelete}
                                    className="cursor-pointer"
                                >
                                    Delete
                                </DropdownMenuLabel>
                                <DropdownMenuLabel
                                    onClick={() =>
                                        saveBlobWithFileSystemAPI(voiceElement.audioBlob, 'sdf.wav')
                                    }
                                    className="cursor-pointer"
                                >
                                    Save
                                </DropdownMenuLabel>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )}
            </div>

            {/* Waveform row */}
            <div className="py-4 px-5 ">
                <div className="flex items-center pr-5 bg-amber-60">
                    <div className="flex flex-col bg-amber-30 mr-4 justify-items-center items-center">
                        <Button onClick={togglePlay} size="icon" className="">
                            {isPlaying ? (
                                <Pause className="w-5 h-5" />
                            ) : (
                                <Play className="w-5 h-5" />
                            )}
                        </Button>
                    </div>
                    <div ref={waveformRef} className="flex-1 rounded-md" />{' '}
                    {audioDuration !== null && (
                        <div className="text-sm text-muted-foreground ml-5">
                            {audioDuration.toFixed(2)} sec
                        </div>
                    )}
                </div>{' '}
            </div>
        </Card>
    )
}

export default Waveform
